import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { eur, fmtDateTime } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, Megaphone, Send, Users, Mail, MessageCircle, MonitorSmartphone, Sparkles, Trash2, CalendarClock } from "lucide-react";

const CHANNELS = [
  { v: "in_app", l: "In-app", icon: MonitorSmartphone },
  { v: "email", l: "Email", icon: Mail },
  { v: "whatsapp", l: "WhatsApp", icon: MessageCircle },
];
const CH_LABEL = { in_app: "In-app", email: "Email", whatsapp: "WhatsApp" };

const emptyCampaign = {
  name: "", channel: "email", subject: "", body: "",
  filters: { tags: "", new_within_days: "", visited_within_days: "", not_visited_days: "", min_spend: "", max_spend: "" },
};

const KpiConsent = ({ label, value, total, icon: Icon }) => (
  <Card className="p-4 flex items-center gap-3">
    <div className="h-10 w-10 rounded-lg bg-[hsl(var(--hi-gold-soft))] flex items-center justify-center"><Icon className="h-5 w-5 text-[hsl(var(--hi-gold))]" /></div>
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-heading text-xl font-semibold tabular">{value ?? "—"}<span className="text-sm text-muted-foreground font-normal"> / {total ?? "—"}</span></div>
    </div>
  </Card>
);

export default function Marketing() {
  const [campaigns, setCampaigns] = useState(null);
  const [consents, setConsents] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCampaign);
  const [editId, setEditId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, cons] = await Promise.all([
        api.get("/marketing/campaigns"),
        api.get("/marketing/consents"),
      ]);
      setCampaigns(c.data);
      setConsents(cons.data);
    } catch (e) { toast.error("Errore nel caricamento"); setCampaigns([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cleanFilters = (f) => {
    const out = {};
    if (f.tags) out.tags = f.tags.split(",").map((t) => t.trim()).filter(Boolean);
    ["new_within_days", "visited_within_days", "not_visited_days"].forEach((k) => { if (f[k] !== "" && f[k] != null) out[k] = Number(f[k]); });
    ["min_spend", "max_spend"].forEach((k) => { if (f[k] !== "" && f[k] != null) out[k] = Number(f[k]); });
    return out;
  };

  const openNew = () => { setForm(emptyCampaign); setEditId(null); setPreview(null); setScheduleAt(""); setOpen(true); };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/marketing/segment/preview", { channel: form.channel, filters: cleanFilters(form.filters) });
      setPreview(data);
    } catch (e) { toast.error("Errore anteprima segmento"); }
    finally { setPreviewing(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Dai un nome alla campagna"); return; }
    if (!form.body.trim()) { toast.error("Scrivi il testo del messaggio"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, channel: form.channel, subject: form.subject, body: form.body, filters: cleanFilters(form.filters) };
      let id = editId;
      if (editId) await api.put(`/marketing/campaigns/${editId}`, payload);
      else { const { data } = await api.post("/marketing/campaigns", payload); id = data.id; }
      toast.success("Campagna salvata");
      setEditId(id);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!editId) { toast.error("Salva prima la campagna"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/marketing/campaigns/${editId}/send-test`);
      toast.success(`Invio di prova: ${data.sent} messaggi (sandbox) · ${data.skipped_no_consent} senza consenso`);
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
    finally { setSaving(false); }
  };

  const scheduleSend = async () => {
    if (!editId) { toast.error("Salva prima la campagna"); return; }
    if (!scheduleAt) { toast.error("Scegli data e ora"); return; }
    setSaving(true);
    try {
      await api.post(`/marketing/campaigns/${editId}/schedule`, { scheduled_for: new Date(scheduleAt).toISOString() });
      toast.success("Campagna programmata (sandbox)");
      setOpen(false); setScheduleAt(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const unschedule = async (id) => {
    try { await api.post(`/marketing/campaigns/${id}/unschedule`); toast.success("Programmazione annullata"); load(); }
    catch (e) { toast.error("Errore"); }
  };

  const removeCampaign = async (id) => {
    try { await api.delete(`/marketing/campaigns/${id}`); toast.success("Campagna eliminata"); load(); }
    catch (e) { toast.error("Errore"); }
  };

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFilter = (k, v) => setForm((f) => ({ ...f, filters: { ...f.filters, [k]: v } }));

  return (
    <div>
      <PageHeader title="Marketing & Campagne" subtitle="Segmenta i clienti e invia campagne di prova rispettando i consensi">
        <Button onClick={openNew} data-testid="campaign-new"><Plus className="h-4 w-4 mr-1" /> Nuova campagna</Button>
      </PageHeader>

      <div className="rounded-lg border border-[hsl(var(--hi-gold)/0.35)] bg-[hsl(var(--hi-gold-soft)/0.4)] px-4 py-2.5 mb-5 text-sm flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-[hsl(var(--hi-gold))]" />
        Le campagne partono in <strong>Sandbox</strong>: nessun cliente viene contattato davvero. Vengono inviate solo alle clienti con consenso sul canale scelto.
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <KpiConsent label="Consenso Email" value={consents?.email} total={consents?.total} icon={Mail} />
        <KpiConsent label="Consenso WhatsApp" value={consents?.whatsapp} total={consents?.total} icon={MessageCircle} />
        <KpiConsent label="In-app (sempre)" value={consents?.total} total={consents?.total} icon={MonitorSmartphone} />
      </div>

      <h2 className="font-heading text-xl font-semibold mb-3">Campagne</h2>
      {campaigns === null ? (
        <div className="grid gap-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
      ) : campaigns.length === 0 ? (
        <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="campaigns-empty">
          <Megaphone className="h-7 w-7 mx-auto mb-2 opacity-40" />
          Nessuna campagna. Crea la prima per raggiungere i tuoi segmenti di clienti.
        </Card>
      ) : (
        <div className="grid gap-3">
          {campaigns.map((c) => {
            const Icon = (CHANNELS.find((x) => x.v === c.channel) || {}).icon || Mail;
            return (
              <Card key={c.id} className="p-4 flex flex-wrap items-center gap-4" data-testid={`campaign-card-${c.id}`}>
                <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center"><Icon className="h-5 w-5 text-[hsl(var(--hi-gold))]" /></div>
                <div className="flex-1 min-w-[180px]">
                  <div className="font-medium flex items-center gap-2">{c.name}
                    {c.status === "sent" && <Badge className="text-[10px]">Inviata (test)</Badge>}
                    {c.status === "scheduled" && <Badge className="text-[10px] bg-[hsl(var(--hi-status-info)/0.15)] text-[hsl(var(--hi-status-info))]">Programmata</Badge>}
                    {(!c.status || c.status === "draft") && <Badge variant="secondary" className="text-[10px]">Bozza</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {CH_LABEL[c.channel]} · {c.status === "scheduled" && c.scheduled_for ? `invio ${fmtDateTime(c.scheduled_for)}` : `creata ${fmtDateTime(c.created_at)}`}
                  </div>
                </div>
                {c.status === "scheduled" && (
                  <Button variant="outline" size="sm" onClick={() => unschedule(c.id)} data-testid={`campaign-unschedule-${c.id}`}>Annulla invio</Button>
                )}
                {c.stats?.sent != null && (
                  <div className="text-right text-sm">
                    <div className="font-semibold tabular">{c.stats.sent} inviati</div>
                    <div className="text-xs text-muted-foreground">{c.stats.skipped_no_consent} senza consenso</div>
                  </div>
                )}
                <Button variant="destructive" size="icon" className="h-8 w-8" onClick={() => removeCampaign(c.id)} data-testid={`campaign-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
              </Card>
            );
          })}
        </div>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading text-2xl">{editId ? "Campagna" : "Nuova campagna"}</SheetTitle></SheetHeader>
          <div className="space-y-4 mt-6">
            <div className="space-y-1.5"><Label>Nome campagna</Label><Input value={form.name} onChange={(e) => setF("name", e.target.value)} placeholder="Es. Riattivazione clienti dormienti" data-testid="campaign-name" /></div>

            <div className="space-y-1.5">
              <Label>Canale</Label>
              <Select value={form.channel} onValueChange={(v) => { setF("channel", v); setPreview(null); }}>
                <SelectTrigger data-testid="campaign-channel"><SelectValue /></SelectTrigger>
                <SelectContent>{CHANNELS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {form.channel === "email" && (
              <div className="space-y-1.5"><Label>Oggetto</Label><Input value={form.subject} onChange={(e) => setF("subject", e.target.value)} placeholder="Ciao {{nome}}, un pensiero per te" data-testid="campaign-subject" /></div>
            )}
            <div className="space-y-1.5">
              <Label>Messaggio</Label>
              <Textarea value={form.body} onChange={(e) => setF("body", e.target.value)} rows={4} placeholder="Scrivi qui il messaggio. Variabili: {{nome}}, {{salone}}" data-testid="campaign-body" />
              <p className="text-[11px] text-muted-foreground">Variabili disponibili: <code>{"{{nome}}"}</code>, <code>{"{{salone}}"}</code></p>
            </div>

            <div className="border-t border-border pt-4 space-y-3">
              <Label className="text-base flex items-center gap-1.5"><Users className="h-4 w-4" /> Segmento clienti</Label>
              <div className="space-y-1.5"><Label className="text-xs">Tag (separati da virgola)</Label><Input value={form.filters.tags} onChange={(e) => setFilter("tags", e.target.value)} placeholder="VIP, Colore" data-testid="filter-tags" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Nuovi da (giorni)</Label><Input type="number" value={form.filters.new_within_days} onChange={(e) => setFilter("new_within_days", e.target.value)} placeholder="es. 30" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Visti negli ultimi (gg)</Label><Input type="number" value={form.filters.visited_within_days} onChange={(e) => setFilter("visited_within_days", e.target.value)} placeholder="es. 60" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Non visti da (gg)</Label><Input type="number" value={form.filters.not_visited_days} onChange={(e) => setFilter("not_visited_days", e.target.value)} placeholder="es. 90" /></div>
                <div />
                <div className="space-y-1.5"><Label className="text-xs">Spesa min (€)</Label><Input type="number" value={form.filters.min_spend} onChange={(e) => setFilter("min_spend", e.target.value)} placeholder="0" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Spesa max (€)</Label><Input type="number" value={form.filters.max_spend} onChange={(e) => setFilter("max_spend", e.target.value)} placeholder="—" /></div>
              </div>
              <Button variant="outline" size="sm" onClick={runPreview} disabled={previewing} data-testid="campaign-preview-btn">
                <Users className="h-4 w-4 mr-1" /> {previewing ? "Calcolo..." : "Anteprima destinatari"}
              </Button>

              {preview && (
                <Card className="p-3 mt-1 space-y-2" data-testid="segment-preview">
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div><span className="text-muted-foreground">Trovati: </span><strong>{preview.matched}</strong></div>
                    <div><span className="text-muted-foreground">Raggiungibili: </span><strong className="text-[hsl(var(--hi-status-success))]">{preview.reachable}</strong></div>
                    <div><span className="text-muted-foreground">Senza consenso: </span><strong className="text-destructive">{preview.no_consent}</strong></div>
                  </div>
                  <div className="max-h-40 overflow-y-auto hi-scroll divide-y divide-border/60">
                    {preview.sample.map((r) => (
                      <div key={r.id} className="flex items-center justify-between py-1.5 text-xs">
                        <span>{r.name}{preview.can_economics && r.spend != null ? ` · ${eur(r.spend)}` : ""}</span>
                        <Badge variant={r.consented ? "secondary" : "outline"} className={`text-[10px] ${r.consented ? "text-[hsl(var(--hi-status-success))]" : "text-muted-foreground"}`}>{r.consented ? "consenso" : "no consenso"}</Badge>
                      </div>
                    ))}
                    {preview.sample.length === 0 && <div className="py-3 text-center text-xs text-muted-foreground">Nessun cliente in questo segmento.</div>}
                  </div>
                </Card>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <Button className="flex-1" variant="outline" onClick={save} disabled={saving} data-testid="campaign-save">Salva bozza</Button>
              <Button className="flex-1" onClick={sendTest} disabled={saving || !editId} data-testid="campaign-send-test"><Send className="h-4 w-4 mr-1" /> Invia (test)</Button>
            </div>

            <div className="border-t border-border pt-3 space-y-2">
              <Label className="flex items-center gap-1.5"><CalendarClock className="h-4 w-4" /> Programma invio (sandbox)</Label>
              <div className="flex gap-2">
                <Input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} data-testid="campaign-schedule-at" />
                <Button variant="secondary" onClick={scheduleSend} disabled={saving || !editId} data-testid="campaign-schedule-btn">Programma</Button>
              </div>
              <p className="text-[11px] text-muted-foreground">Salva prima la bozza, poi scegli data e ora: lo scheduler invierà automaticamente (sempre in sandbox).</p>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
